import { useCallback, useEffect, useMemo, useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { useMergeLink } from '@mergeapi/react-merge-link'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { AlertTriangle, CheckCircle, Download, Eye, Loader2, Lock, RefreshCw } from 'lucide-react'
import { client } from '@/client/client.gen'
import { useToast } from '@/hooks/use-toast'
import { useAuth } from '@/hooks/use-auth'
import { SettingsPageSkeleton } from './-components/skeleton'
import { CrmUserMappings } from './-components/crm-user-mappings'

export const Route = createFileRoute('/_authed/_user/settings/integrations')({
  component: IntegrationsPage,
  pendingComponent: SettingsPageSkeleton,
  beforeLoad: () => ({
    getTitle: () => 'Integrations',
    getDescription: () => 'Connect your CRM and other tools to sync data automatically',
  }),
})

interface CrmProvider {
  name: string
  slug: string
  logo: string
  manual?: boolean
}

const CRM_PROVIDERS: CrmProvider[] = [
  { name: 'Salesforce', slug: 'salesforce', logo: `${import.meta.env.BASE_URL}assets/crm-logos/salesforce.png` },
  { name: 'HubSpot', slug: 'hubspot', logo: `${import.meta.env.BASE_URL}assets/crm-logos/hubspot.png` },
  { name: 'Pipeliner', slug: 'pipeliner', logo: `${import.meta.env.BASE_URL}assets/crm-logos/pipeliner.png` },
  { name: 'Microsoft Dynamics', slug: 'microsoft-dynamics-365-sales', logo: `${import.meta.env.BASE_URL}assets/crm-logos/dynamics.png` },
  { name: 'Epicor Prophet 21', slug: 'epicor-p21', logo: `${import.meta.env.BASE_URL}assets/crm-logos/epicor.png`, manual: true },
  { name: 'Epicor Eclipse', slug: 'epicor-eclipse', logo: `${import.meta.env.BASE_URL}assets/crm-logos/epicor.png`, manual: true },
  { name: 'Epicor Kinetic', slug: 'epicor-kinetic', logo: `${import.meta.env.BASE_URL}assets/crm-logos/epicor.png`, manual: true },
]

interface ExchangeTokenResponse {
  integrationId: number
  integrationName: string | undefined
}

interface CrmConnectButtonProps {
  slug: string
  onConnected: (integrationId: number, integrationName: string | undefined) => void
  disabled?: boolean
}

function CrmConnectButton({ slug, onConnected, disabled }: CrmConnectButtonProps) {
  const { toast } = useToast()
  const [linkToken, setLinkToken] = useState<string>()
  const [showConfirm, setShowConfirm] = useState(false)

  const onSuccess = useCallback(
    async (publicToken: string) => {
      try {
        const response = await client.post<ExchangeTokenResponse>({
          url: '/app/integrations/merge/exchange-token',
          body: { publicToken },
          security: [{ in: 'cookie', name: 'fastapiusersauth', type: 'apiKey' }],
        })
        const data = response.data as ExchangeTokenResponse | undefined
        onConnected(data?.integrationId ?? 0, data?.integrationName)
        toast({
          title: 'CRM Connected',
          description: 'Your CRM has been successfully connected.',
          variant: 'success',
        })
      }
      catch {
        toast({
          title: 'Connection Failed',
          description: 'Failed to save CRM connection. Please try again.',
          variant: 'destructive',
        })
      }
    },
    [toast, onConnected],
  )

  const onExit = useCallback(() => {
    setLinkToken(undefined)
  }, [])

  const { open, isReady } = useMergeLink({
    linkToken,
    onSuccess,
    onExit,
  })

  // Show dialog immediately on click; fetch token in background so SDK loads
  // while user reads the confirmation. "Acknowledge" is disabled until ready.
  const handleConnect = useCallback(async () => {
    setShowConfirm(true)
    if (linkToken) {
      return
    }
    try {
      const response = await client.post<{ linkToken: string }>({
        url: '/app/integrations/merge/link-token',
        body: { integration: slug },
        security: [{ in: 'cookie', name: 'fastapiusersauth', type: 'apiKey' }],
      })
      const data = response.data as { linkToken: string } | undefined
      if (data?.linkToken) {
        setLinkToken(data.linkToken)
      }
    }
    catch {
      toast({
        title: 'Failed to initialize',
        description: 'Could not initialize CRM connection. Please try again.',
        variant: 'destructive',
      })
      setShowConfirm(false)
    }
  }, [linkToken, slug, toast])

  const handleAcknowledge = useCallback(() => {
    setShowConfirm(false)
    open()
  }, [open])

  return (
    <>
      <Button variant="outline" onClick={handleConnect} disabled={disabled}>
        Connect
      </Button>

      <AlertDialog open={showConfirm} onOpenChange={setShowConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5 text-indigo-600" />
              Read-Only Integration
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div>
                <p>
                  {'SUPPLYCO AI will only '}
                  <strong className="text-foreground">read</strong>
                  {' data from your CRM. We will never create, modify, or delete any records.'}
                </p>
                <p className="mt-3 font-medium text-foreground">We will import:</p>
                <ul className="mt-1 ml-4 list-disc space-y-1">
                  <li>Accounts</li>
                  <li>Contacts</li>
                  <li>Opportunities</li>
                </ul>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Go Back</AlertDialogCancel>
            <AlertDialogAction onClick={handleAcknowledge} disabled={!isReady}>
              {isReady
                ? 'Acknowledge & Continue'
                : (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Initializing...
                    </>
                  )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}

function ManualConnectButton() {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span tabIndex={0}>
            <Button variant="outline" disabled className="pointer-events-none">
              Connect
            </Button>
          </span>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">
          Email
          {' '}
          <span className="font-medium">support@supplyco.ai</span>
          {' '}
          to set up this integration.
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

function useCountdown(targetDate: Date | undefined) {
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    if (!targetDate || targetDate.getTime() <= Date.now()) {
      // Nothing to count down — skip the interval entirely. This matters for
      // integrations whose createdAt is already >24h in the past, where the
      // caller passes a deadline but we'd otherwise re-render every minute
      // for a useMemo result that's always undefined.
      return
    }
    const id = setInterval(() => {
      const currentNow = Date.now()
      setNow(currentNow)
      if (targetDate.getTime() <= currentNow) {
        clearInterval(id)
      }
    }, 60_000)
    return () => clearInterval(id)
  }, [targetDate])

  return useMemo(() => {
    if (!targetDate)
      return
    const diff = targetDate.getTime() - now
    if (diff <= 0)
      return
    const hours = Math.floor(diff / 3_600_000)
    const minutes = Math.ceil((diff % 3_600_000) / 60_000)
    if (hours > 0)
      return `~${hours}h ${minutes}m remaining`
    return `~${minutes}m remaining`
  }, [targetDate, now])
}

interface IntegrationStatus {
  isConnected: boolean
  isSyncInProgress: boolean
  integrationId: number | undefined
  integrationName: string | undefined
  integrationSlug: string | undefined
  createdAt: string | undefined
  lastSyncedAt: string | undefined
  accountsSynced: number
  contactsSynced: number
  lastSyncFailedCount: number
  lastSyncLogId: number | undefined
}

function IntegrationsPage() {
  const { toast } = useToast()
  const { isOrgAdmin, isTrialOrg } = useAuth()
  const [fetchComplete, setFetchComplete] = useState(false)
  const [isConnected, setIsConnected] = useState(false)
  const [integrationId, setIntegrationId] = useState<number>()
  const [integrationName, setIntegrationName] = useState<string>()
  const [integrationSlug, setIntegrationSlug] = useState<string>()
  const [createdAt, setCreatedAt] = useState<Date>()
  const [lastSyncedAt, setLastSyncedAt] = useState<Date>()
  const [accountsSynced, setAccountsSynced] = useState(0)
  const [contactsSynced, setContactsSynced] = useState(0)
  const [lastSyncFailedCount, setLastSyncFailedCount] = useState(0)
  const [lastSyncLogId, setLastSyncLogId] = useState<number>()
  const [isSyncing, setIsSyncing] = useState(false)

  // isLoading: true until either the fetch completes or we skip it (non-admin).
  const isLoading = isOrgAdmin && !fetchComplete

  const syncDeadline = useMemo(
    () => createdAt ? new Date(createdAt.getTime() + 24 * 60 * 60 * 1000) : undefined,
    [createdAt],
  )
  // useCountdown returns undefined when the deadline has passed or is absent,
  // so we can derive "within initial sync window" directly from it.
  const countdown = useCountdown(syncDeadline)
  const isInitialSync = countdown !== undefined

  useEffect(() => {
    if (!isOrgAdmin) {
      return
    }

    const fetchInitialData = async () => {
      try {
        const statusResponse = await client.get<IntegrationStatus>({
          url: '/app/integrations/merge/status',
          security: [{ in: 'cookie', name: 'fastapiusersauth', type: 'apiKey' }],
        })

        const statusData = statusResponse.data as IntegrationStatus | undefined
        if (statusData?.isConnected) {
          setIsConnected(true)
          setIntegrationId(statusData.integrationId)
          setIntegrationName(statusData.integrationName)
          setIntegrationSlug(statusData.integrationSlug)
          if (statusData.createdAt) {
            setCreatedAt(new Date(statusData.createdAt))
          }
          if (statusData.lastSyncedAt) {
            setLastSyncedAt(new Date(statusData.lastSyncedAt))
          }
          setAccountsSynced(statusData.accountsSynced)
          setContactsSynced(statusData.contactsSynced)
          setLastSyncFailedCount(statusData.lastSyncFailedCount)
          setLastSyncLogId(statusData.lastSyncLogId)
          setIsSyncing(statusData.isSyncInProgress)
        }
      }
      catch {
        toast({
          title: 'Failed to initialize',
          description: 'Could not load integration status. Please try again.',
          variant: 'destructive',
        })
      }
      finally {
        setFetchComplete(true)
      }
    }

    fetchInitialData()
  }, [toast, isOrgAdmin])

  const handleSync = useCallback(async () => {
    if (!integrationId || isSyncing) {
      return
    }

    setIsSyncing(true)
    try {
      const result = await client.post({
        url: '/app/integrations/merge/sync',
        body: { integrationId },
        security: [{ in: 'cookie', name: 'fastapiusersauth', type: 'apiKey' }],
      })

      if (result.error) {
        if (result.response?.status === 409) {
          toast({
            title: 'Sync In Progress',
            description: 'A sync is already in progress for your organization.',
          })
        }
        else {
          toast({
            title: 'Sync Failed',
            description: 'Failed to start CRM sync. Please try again.',
            variant: 'destructive',
          })
        }
        setIsSyncing(false)
      }
      else {
        toast({
          title: 'Sync Started',
          description: 'Your CRM data is being synced. This may take a few minutes.',
          variant: 'success',
        })
      }
    }
    catch {
      toast({
        title: 'Sync Failed',
        description: 'Failed to start CRM sync. Please try again.',
        variant: 'destructive',
      })
      setIsSyncing(false)
    }
  }, [integrationId, isSyncing, toast])

  if (!isOrgAdmin) {
    return (
      <div className="space-y-4">
        <Card className="p-6">
          <div className="flex flex-col items-center gap-4 text-center">
            <Lock className="h-12 w-12 text-muted-foreground" />
            <div>
              <h3 className="font-semibold">Admin Access Required</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Please ask an admin to configure this
              </p>
            </div>
          </div>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {isTrialOrg && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          <strong>CRM integrations are available on paid plans.</strong>
          {' '}
          Upgrade to connect your CRM and sync accounts automatically.
        </div>
      )}
      <div className="space-y-3">
        {CRM_PROVIDERS.map((provider) => {
          const isThisConnected = isConnected && integrationSlug === provider.slug

          return (
            <Card key={provider.slug} className={`transition-colors ${isTrialOrg ? 'opacity-50 pointer-events-none' : 'hover:bg-accent/50'}`}>
              <div className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <img
                      src={provider.logo}
                      alt={provider.name}
                      width={40}
                      height={40}
                      className="rounded"
                    />
                    <div>
                      <div className="font-medium">{provider.name}</div>
                      <div className="text-sm text-muted-foreground">
                        <ConnectionStatus
                          isTrialOrg={isTrialOrg}
                          isThisConnected={isThisConnected}
                          isInitialSync={isInitialSync}
                          isConnected={isConnected}
                          countdown={countdown}
                        />
                      </div>
                      {isThisConnected && !isInitialSync && lastSyncedAt && (
                        <div className="text-xs text-muted-foreground">
                          Last synced:
                          {' '}
                          {lastSyncedAt.toLocaleString()}
                        </div>
                      )}
                      {isThisConnected && !isInitialSync && (accountsSynced > 0 || contactsSynced > 0) && (
                        <div className="text-xs text-muted-foreground">
                          {accountsSynced}
                          {' '}
                          accounts,
                          {' '}
                          {contactsSynced}
                          {' '}
                          contacts synced
                          {lastSyncFailedCount > 0 && (
                            <span className="text-amber-600 ml-1">
                              (
                              {lastSyncFailedCount}
                              {' '}
                              failed)
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    {isThisConnected && !isInitialSync && lastSyncFailedCount > 0 && lastSyncLogId && (
                      <a
                        href={`${client.getConfig().baseUrl}/app/integrations/merge/logs/${lastSyncLogId}/failed-accounts.csv`}
                        className="inline-flex items-center gap-1 text-xs text-amber-600 hover:text-amber-700"
                        download
                      >
                        <AlertTriangle className="h-3 w-3" />
                        <Download className="h-3 w-3" />
                        Failed imports
                      </a>
                    )}

                    {isThisConnected && (
                      <>
                        {isInitialSync
                          ? <Loader2 className="h-5 w-5 text-indigo-600 animate-spin" />
                          : <CheckCircle className="h-5 w-5 text-green-600" />}
                        {(!isInitialSync || import.meta.env.DEV) && (
                          <Button variant="outline" size="sm" onClick={handleSync} disabled={isSyncing}>
                            <RefreshCw className={`mr-2 h-4 w-4 ${isSyncing ? 'animate-spin' : ''}`} />
                            {isSyncing ? 'Syncing...' : 'Sync Now'}
                          </Button>
                        )}
                      </>
                    )}
                    {!isThisConnected && provider.manual && (
                      <ManualConnectButton />
                    )}
                    {!isThisConnected && !provider.manual && (
                      <>
                        {isLoading && (
                          <Button variant="outline" disabled>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Loading...
                          </Button>
                        )}
                        {!isLoading && !isConnected && (
                          <CrmConnectButton
                            slug={provider.slug}
                            onConnected={(id, name) => {
                              setIsConnected(true)
                              setIntegrationId(id)
                              setIntegrationName(name)
                              setIntegrationSlug(provider.slug)
                              setCreatedAt(new Date())
                            }}
                          />
                        )}
                        {!isLoading && isConnected && (
                          <Button variant="outline" disabled>Connected elsewhere</Button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            </Card>
          )
        })}
      </div>

      {/* User Mappings - show when connected and not in initial sync */}
      {isConnected && !isInitialSync && (
        <CrmUserMappings integrationName={integrationName} />
      )}
    </div>
  )
}

function ConnectionStatus({ isTrialOrg, isThisConnected, isInitialSync, isConnected, countdown }: {
  isTrialOrg: boolean
  isThisConnected: boolean
  isInitialSync: boolean
  isConnected: boolean
  countdown: string | undefined
}) {
  if (isTrialOrg)
    return <>Available on paid plans</>
  if (isThisConnected && isInitialSync)
    return <>{`Initial sync in progress...${countdown ? ` (${countdown})` : ''}`}</>
  if (isThisConnected)
    return <>Connected</>
  if (isConnected)
    return <>Another CRM is connected</>
  return <>Not connected</>
}
